/**
 * PAPPO — Admin Service (/api/admin)
 * Revisão de KYC, break-glass e crypto-shredding. Só role admin.
 */

import { apiGet, apiPost } from './api';
import type {
  BreakGlassAberto,
  BreakGlassDado,
  KycPendente,
} from '../types/kyc';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/** Fila de verificações de identidade aguardando revisão. */
export async function listarKycPendentes() {
  return apiGet<{ pendentes: KycPendente[] }>('/admin/kyc/pendentes');
}

/**
 * URL autenticada da imagem (rg|selfie). Como a rota devolve bytes com o JWT
 * no header, buscamos via fetch e criamos um object URL para o <img>.
 */
export async function carregarKycImagem(
  verificationId: number,
  tipo: 'rg' | 'selfie'
): Promise<string | null> {
  const token = localStorage.getItem('pappo_access_token');
  try {
    const res = await fetch(
      `${API_BASE}/admin/kyc/${verificationId}/imagem/${tipo}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

export async function aprovarKyc(verificationId: number) {
  return apiPost<{ user_id: number; account_level: number }>(
    `/admin/kyc/${verificationId}/aprovar`
  );
}

export async function rejeitarKyc(verificationId: number, reason: string) {
  return apiPost<{ user_id: number }>(`/admin/kyc/${verificationId}/rejeitar`, {
    reason,
  });
}

/** Abre uma janela de 5 min de acesso a dado cifrado (motivo obrigatório). */
export async function abrirBreakGlass(payload: {
  target_user: number;
  data_type: string;
  reason: string;
}) {
  return apiPost<BreakGlassAberto>('/admin/break-glass', payload);
}

/** Enquanto a janela está aberta, obtém o dado decifrado (marca 'used'). */
export async function obterBreakGlassDado(bgId: number) {
  return apiGet<BreakGlassDado>(`/admin/break-glass/${bgId}/dado`);
}

/** Crypto-shredding: destrói a DEK do usuário e desativa a conta. */
export async function esquecerUsuario(targetUser: number) {
  return apiPost<{ target_user: number }>(
    `/admin/usuarios/${targetUser}/esquecer`
  );
}
